// =============================================================================
// itemValidation.ts — validates parsed JSON items against the expected schema
// for each test slug before they're shown for review or saved to the DB.
//
// Catches the most common LLM mistakes:
//   - Missing required fields
//   - correct_answer not in options
//   - Watson-Glaser items missing the section tag
//   - Cappfinity numerical items missing chartConfig
//   - SJT items where correct_answer isn't valid most/least JSON
//
// Returns { ok, errors[] } per item so the UI can show validation messages
// inline and let Jamie fix issues before approving.
// =============================================================================

export interface DraftItem {
  context?: string | null;
  prompt?: string;
  options?: { id: string; text?: string; figure?: unknown }[];
  correct_answer?: string;
  scoring_metadata?: Record<string, unknown>;
}

export interface ValidationResult {
  ok: boolean;
  errors: string[];
}

export function validateItem(testSlug: string, item: DraftItem): ValidationResult {
  const errors: string[] = [];

  // ── Common required fields ──────────────────────────────────────────
  if (!item.prompt || typeof item.prompt !== 'string' || item.prompt.trim().length === 0) {
    errors.push('Missing or empty prompt.');
  }
  if (!Array.isArray(item.options) || item.options.length < 2) {
    errors.push('Options array must have at least 2 entries.');
  }
  if (!item.correct_answer || typeof item.correct_answer !== 'string') {
    errors.push('Missing correct_answer.');
  }

  // ── Per-engine schema checks ────────────────────────────────────────
  const meta = item.scoring_metadata ?? {};

  if (testSlug.startsWith('sjt-')) {
    // SJT: correct_answer is JSON {"most":"a","least":"d"}
    if (item.correct_answer) {
      let parsed: { most?: string; least?: string } | null = null;
      try { parsed = JSON.parse(item.correct_answer); } catch { /* fall-through */ }
      if (!parsed || !parsed.most || !parsed.least) {
        errors.push('SJT correct_answer must be JSON like {"most":"b","least":"d"}.');
      } else if (parsed.most === parsed.least) {
        errors.push('SJT most and least cannot be the same option.');
      } else {
        const ids = (item.options ?? []).map((o) => o.id);
        if (!ids.includes(parsed.most)) errors.push(`SJT most ("${parsed.most}") not in options.`);
        if (!ids.includes(parsed.least)) errors.push(`SJT least ("${parsed.least}") not in options.`);
      }
    }
    if (!item.context || (item.context as string).trim().length < 20) {
      errors.push('SJT items need a workplace scenario in context (≥20 chars).');
    }
    if (typeof meta.competency !== 'string') {
      errors.push('SJT items must tag scoring_metadata.competency.');
    }
  } else if (testSlug === 'verbal-watson-glaser-v1') {
    if (typeof meta.section !== 'string') {
      errors.push('Watson-Glaser items must tag scoring_metadata.section.');
    }
    if (!item.context || (item.context as string).trim().length < 20) {
      errors.push('Watson-Glaser items need a passage in context (≥20 chars).');
    }
    validateCorrectAnswerInOptions(item, errors);
  } else if (testSlug === 'numerical-cappfinity-v1') {
    if (typeof meta.stage !== 'number' || meta.stage < 1 || meta.stage > 3) {
      errors.push('Cappfinity numerical items need scoring_metadata.stage = 1, 2, or 3.');
    }
    const chartConfig = meta.chartConfig as { data?: unknown[] } | undefined;
    if (!chartConfig || !Array.isArray(chartConfig.data) || chartConfig.data.length === 0) {
      errors.push('Cappfinity numerical items need scoring_metadata.chartConfig.data with at least one row.');
    }
    if (typeof meta.chartType !== 'string') {
      errors.push('Cappfinity numerical items need scoring_metadata.chartType.');
    }
    validateCorrectAnswerInOptions(item, errors);
  } else if (testSlug === 'verbal-cappfinity-v1') {
    if (typeof meta.stage !== 'number' || meta.stage < 1 || meta.stage > 3) {
      errors.push('Cappfinity verbal items need scoring_metadata.stage = 1, 2, or 3.');
    }
    if (!item.context || (item.context as string).trim().length < 80) {
      errors.push('Cappfinity verbal items need a reading passage in context (≥80 chars).');
    }
    validateCorrectAnswerInOptions(item, errors);
  } else if (testSlug === 'numerical-talentq-v1') {
    const chartConfig = meta.chartConfig as { data?: unknown[] } | undefined;
    if (!chartConfig || !Array.isArray(chartConfig.data) || chartConfig.data.length === 0) {
      errors.push('Talent Q numerical items need scoring_metadata.chartConfig.data.');
    }
    if (typeof meta.chartType !== 'string') {
      errors.push('Talent Q numerical items need scoring_metadata.chartType.');
    }
    validateCorrectAnswerInOptions(item, errors);
  } else if (testSlug === 'verbal-talentq-v1') {
    if (!item.context || (item.context as string).trim().length < 80) {
      errors.push('Talent Q verbal items need a passage in context (≥80 chars).');
    }
    validateCorrectAnswerInOptions(item, errors);
  } else if (testSlug === 'logical-talentq-v1') {
    if (typeof meta.patternType !== 'string') {
      errors.push('Talent Q logical items need scoring_metadata.patternType.');
    }
    validateCorrectAnswerInOptions(item, errors);
  } else if (testSlug === 'numerical-shl-interactive-v1') {
    if (typeof meta.expectedValue !== 'number') {
      errors.push('SHL Verify Interactive items need scoring_metadata.expectedValue (number).');
    }
    if (meta.widget !== 'keypad' && meta.widget !== 'slider') {
      errors.push('SHL Verify Interactive items need scoring_metadata.widget = "keypad" or "slider".');
    }
    const chartConfig = meta.chartConfig as { data?: unknown[] } | undefined;
    if (!chartConfig || !Array.isArray(chartConfig.data) || chartConfig.data.length === 0) {
      errors.push('SHL Verify Interactive items need scoring_metadata.chartConfig.data.');
    }
  } else if (testSlug === 'personality-hogan-hpi-v1' || testSlug === 'personality-hogan-hds-v1') {
    const loadings = meta.loadings as Record<string, unknown> | undefined;
    if (!loadings || typeof loadings !== 'object' || Object.keys(loadings).length === 0) {
      errors.push('Hogan items must tag scoring_metadata.loadings with at least one scale.');
    } else {
      // Each loading value must be a finite number; primary loading should be |w| ≥ 0.5.
      const allowedScales = testSlug === 'personality-hogan-hpi-v1'
        ? new Set(['adjustment','ambition','sociability','interpersonal-sensitivity','prudence','inquisitive','learning-approach'])
        : new Set(['excitable','skeptical','cautious','reserved','leisurely','bold','mischievous','colorful','imaginative','diligent','dutiful']);
      let maxAbs = 0;
      for (const [scale, weight] of Object.entries(loadings)) {
        if (!allowedScales.has(scale)) {
          errors.push(`Unknown Hogan scale "${scale}". Allowed: ${[...allowedScales].join(', ')}.`);
        }
        if (typeof weight !== 'number' || !Number.isFinite(weight)) {
          errors.push(`Loading for "${scale}" must be a finite number, got ${typeof weight}.`);
        } else {
          maxAbs = Math.max(maxAbs, Math.abs(weight));
        }
      }
      if (maxAbs < 0.5) {
        errors.push('At least one loading should have |weight| ≥ 0.5 (primary scale).');
      }
    }
    // Hogan items don't need a correct_answer (no right answer).
  } else if (testSlug.startsWith('coding-')) {
    if (typeof meta.functionName !== 'string') {
      errors.push('Coding items need scoring_metadata.functionName.');
    }
    if (typeof meta.starterCode !== 'string') {
      errors.push('Coding items need scoring_metadata.starterCode.');
    }
    const testCases = meta.testCases as unknown[] | undefined;
    if (!Array.isArray(testCases) || testCases.length < 3) {
      errors.push('Coding items need scoring_metadata.testCases with ≥3 entries.');
    } else {
      const visibleCount = testCases.filter((tc) => (tc as { visible?: boolean })?.visible).length;
      if (visibleCount === 0) {
        errors.push('At least one test case must be visible: true.');
      }
      if (visibleCount === testCases.length) {
        errors.push('At least one test case must be hidden (visible: false) so submit verifies edge cases.');
      }
    }
    if (typeof meta.language !== 'string') {
      errors.push('Coding items need scoring_metadata.language (e.g. "javascript").');
    }
  } else {
    // Generic check for any other slug
    validateCorrectAnswerInOptions(item, errors);
  }

  // ── Sector tags ─────────────────────────────────────────────────────
  if (!Array.isArray(meta.sectors) || meta.sectors.length === 0) {
    errors.push('Missing scoring_metadata.sectors array (at least one tag required).');
  }

  return { ok: errors.length === 0, errors };
}

function validateCorrectAnswerInOptions(item: DraftItem, errors: string[]): void {
  if (!item.correct_answer || !Array.isArray(item.options)) return;
  const ids = item.options.map((o) => o.id);
  if (!ids.includes(item.correct_answer)) {
    errors.push(`correct_answer "${item.correct_answer}" is not in options [${ids.join(', ')}].`);
  }
}

/** Best-effort JSON parser — strips common LLM wrappers (markdown fences, prose).
 *  Returns the parsed array or throws with a useful error. */
export function parseDraftItems(raw: string): DraftItem[] {
  let trimmed = raw.trim();
  if (!trimmed) throw new Error('Paste the JSON array first.');

  // Strip markdown code fences if present.
  trimmed = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '');

  // Find the first '[' and last ']' so prose surrounding the array doesn't break parse.
  const start = trimmed.indexOf('[');
  const end = trimmed.lastIndexOf(']');
  if (start === -1 || end === -1 || end < start) {
    throw new Error('No JSON array found in the pasted text.');
  }
  const json = trimmed.slice(start, end + 1);
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`JSON parse failed: ${msg}`);
  }
  if (!Array.isArray(parsed)) {
    throw new Error('Top-level value must be an array of items.');
  }
  return parsed as DraftItem[];
}
